import { ReactNode } from "react";

import dayjs from "dayjs";
import "dayjs/locale/es-mx";
import { Grid, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { NumberFormatValues, NumericFormat } from "react-number-format";

export const FieldRow = ({ label, children }: { label: string; children: ReactNode }) => (
    <Grid container spacing={2} sx={{ alignItems: "center" }}>
        <Grid size={4}>
            <Typography variant="body1" sx={{ color: "main3", fontWeight: 700, opacity: 0.55 }}>
                {label}
            </Typography>
        </Grid>
        <Grid size={8}>{children}</Grid>
    </Grid>
);

export const ReadonlyValue = ({ value }: { value: ReactNode }) => (
    <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value}
    </Typography>
);

interface MoneyInputProps {
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
    name?: string;
    decimalScale?: number;
}

export const MoneyInput = ({ value, onChange, disabled, name, decimalScale = 2 }: MoneyInputProps) => (
    <NumericFormat
        fullWidth
        size="small"
        name={name}
        prefix="$"
        thousandSeparator
        decimalScale={decimalScale}
        displayType="input"
        customInput={TextField}
        autoComplete="off"
        value={value}
        onValueChange={(event: NumberFormatValues) => onChange(event.floatValue ?? 0)}
        disabled={disabled}
    />
);

interface TextInputProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    name?: string;
    multiline?: boolean;
}

export const TextInput = ({ value, onChange, disabled, name, multiline }: TextInputProps) => (
    <TextField
        fullWidth
        size="small"
        name={name}
        autoComplete="off"
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
    />
);

interface DateInputProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const DateInput = ({ value, onChange, disabled }: DateInputProps) => (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es-mx">
        <DatePicker
            format="DD/MM/YYYY"
            value={dayjs(value).isValid() ? dayjs(value) : null}
            onChange={(date) => onChange(date?.isValid() ? date.toISOString() : "")}
            slotProps={{ textField: { fullWidth: true, size: "small" } }}
            disabled={disabled}
        />
    </LocalizationProvider>
);
